import { isStaffOrAbove } from "@excel-center/shared";
import { Toast } from "@heroui/react";
import { useEffect } from "react";
import { selectRole } from "@/src/auth/store/auth.selectors";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { getColorScheme, getColorSchemePreference, getThemePreview } from "@/src/common/store/general-selectors";
import { setColorScheme } from "@/src/common/store/general-slice";

interface AppThemeProviderProps {
	children: React.ReactNode;
}

/**
 * HeroUI v3 does not need a provider — tokens live in CSS via `@heroui/styles`.
 * This component syncs the colour scheme (light/dark) and the role-based
 * theme (blue for public/guest/student, green for staff/admin/super_admin)
 * onto the document element, and mounts the Toast portal.
 */
export function AppThemeProvider({ children }: AppThemeProviderProps) {
	const colorScheme = useAppSelector(getColorScheme);
	const colorSchemePreference = useAppSelector(getColorSchemePreference);
	const role = useAppSelector(selectRole);
	const themePreview = useAppSelector(getThemePreview);
	const dispatch = useAppDispatch();

	useEffect(() => {
		const root = document.documentElement;
		root.classList.remove("light", "dark");
		root.classList.add(colorScheme);
		root.setAttribute("data-theme", colorScheme);
	}, [colorScheme]);

	useEffect(() => {
		if (colorSchemePreference === "light" || colorSchemePreference === "dark") {
			dispatch(setColorScheme(colorSchemePreference));
			return;
		}
		const mql = window.matchMedia("(prefers-color-scheme: dark)");
		dispatch(setColorScheme(mql.matches ? "dark" : "light"));
		const onChange = (event: MediaQueryListEvent) => {
			dispatch(setColorScheme(event.matches ? "dark" : "light"));
		};
		mql.addEventListener("change", onChange);
		return () => mql.removeEventListener("change", onChange);
	}, [dispatch, colorSchemePreference]);

	useEffect(() => {
		const roleDerived = isStaffOrAbove(role) ? "green" : "blue";
		document.documentElement.setAttribute("data-role-theme", themePreview ?? roleDerived);
	}, [role, themePreview]);

	return (
		<>
			{children}
			<Toast.Provider />
		</>
	);
}
